import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiError, fmtMinutes, currentMonth } from "@/lib/format";
import { downloadBlob } from "@/lib/download";
import { PageHeader } from "@/components/Page";

function hm(min) {
  const sign = min < 0 ? "-" : "";
  const m = Math.abs(Math.round(min));
  return `${sign}${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
}

export default function Reports() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/reports/overtime", { params: { month } });
    setData(r.data);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const csv = async () => {
    try { await downloadBlob(`/admin/reports/csv?month=${month}`, `report_${month}.csv`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pdf = async () => {
    try { await downloadBlob(`/admin/reports/pdf?month=${month}`, `teamreport_${month}.pdf`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Reports & Überstunden"
        subtitle="Monatsauswertung mit Überstunden und Monatsübertrag."
        testid="reports-header"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={csv} data-testid="report-csv"><FileDown className="h-4 w-4 mr-2" /> CSV</Button>
            <Button onClick={pdf} data-testid="report-pdf" className="brand-bg text-white hover:opacity-90"><FileText className="h-4 w-4 mr-2" /> PDF</Button>
          </div>
        }
      />
      <div className="mb-4">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="report-month" />
      </div>

      <Card className="border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Mitarbeiter</TableHead>
              <TableHead>Ist</TableHead>
              <TableHead>Soll</TableHead>
              <TableHead>Überstunden</TableHead>
              <TableHead>Übertrag Vormonat</TableHead>
              <TableHead>Gesamt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.rows || []).map((r) => (
              <TableRow key={r.user_id} data-testid={`report-row-${r.user_id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="font-mono">{fmtMinutes(r.ist)}</TableCell>
                <TableCell className="font-mono text-slate-500">{fmtMinutes(r.soll)}</TableCell>
                <TableCell className={`font-mono ${r.over < 0 ? "text-rose-600" : "text-emerald-600"}`}>{hm(r.over)}</TableCell>
                <TableCell className={`font-mono ${r.carry < 0 ? "text-rose-600" : "text-emerald-600"}`}>{hm(r.carry)}</TableCell>
                <TableCell className={`font-mono font-bold ${r.total < 0 ? "text-rose-600" : "text-emerald-600"}`}>{hm(r.total)}</TableCell>
              </TableRow>
            ))}
            {(!data?.rows || data.rows.length === 0) && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Keine Mitarbeiterdaten</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
